import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { MdSearch } from 'react-icons/md'
import { useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import * as Yup from 'yup'

import Autocomplete from 'shared/components/Autocomplete'
import Button from 'shared/components/Button'
import Slider from 'shared/components/Slider'
import LoteSwitch from '../components/LoteSwitch'
import SnackBar from 'shared/components/SnackBar'
import useDebounce from 'shared/hooks/useDebounce'
import ICidade from 'shared/modules/cidades/domain/entities/Cidade'
import { IFindByNome } from 'shared/modules/cidades/domain/usecases/FindByNome'
import { registerEvent } from 'shared/services/TrackingGAService'
import ContainerPage from 'shared/components/ContainerPageNew'
import Form from 'shared/components/Form'
import Input from 'shared/components/Input'
import { colors } from 'shared/styles/colors'
import IProduto from 'modules/lotes/domain/entities/IProduto'
import { IFindAllProdutos } from 'modules/lotes/domain/usecases/FindAllProdutos'
import { useFiltros } from 'modules/lotes/hooks/filtros'

import { ContainerAutocomplete } from './styles'

type TProps = {
  id: number
  setId: (id: number) => void
  origem: string
  setOrigem: (origem: number) => void
  destino: string
  setDestino: (destino: number) => void
  valor: number
  setValor: (valor: number) => void
  raioOrigem: number
  setRaioOrigem: (raioOrigem: number) => void
  raioDestino: number
  setRaioDestino: (raioDestino: number) => void
  origemCompleta: ICidade
  setOrigemCompleta: (origemCompleta: ICidade) => void
  destinoCompleto: ICidade
  setDestinoCompleto: (destinoCompleto: ICidade) => void
  findByNome: IFindByNome
  findAllProdutos: IFindAllProdutos
}

const marks = [
  {
    value: 0,
    label: 'R$ 0',
  },
  {
    value: 1000,
    label: 'R$ 1000',
  },
]

const marksRaio = [
  {
    value: 0,
    label: '0km',
  },
  {
    value: 100,
    label: '100km',
  },
  {
    value: 200,
    label: '200km',
  },
  {
    value: 300,
    label: '300km',
  },
]

const Filtros: FunctionComponent<TProps> = ({
  origemCompleta,
  setOrigemCompleta,
  destinoCompleto,
  setDestinoCompleto,
  findByNome,
  findAllProdutos,
}) => {
  const {
    id,
    valor,
    destino,
    origem,
    raioOrigem,
    raioDestino,
    produto,
    lotesDirecionados,
    setRaioOrigem,
    setRaioDestino,
    setId,
    setDestino,
    setOrigem,
    setValor,
    setProduto,
    setLotesDirecionados,
    setLastFilter,
  } = useFiltros()
  const [loading, setLoading] = useState(false)
  const [idInput, setIdInput] = useState(id)
  const [valorSlider, setValorSlider] = useState<number | number[]>(valor || 0)
  const [valorSliderOrigem, setValorSliderOrigem] = useState<number | number[]>(
    raioOrigem ?? 100,
  )
  const [valorSliderDestino, setValorSliderDestino] = useState<
    number | number[]
  >(raioDestino ?? 100)
  const [cidadesOrigem, setCidadesOrigem] = useState<ICidade[]>([])
  const [cidadesDestino, setCidadesDestino] = useState<ICidade[]>([])
  const [origemInput, setOrigemInput] = useState(origem || '')
  const [destinoInput, setDestinoInput] = useState(destino || '')
  const [origemAutocomplete, setOrigemAutocomplete] = useState<ICidade>(
    origemCompleta || ({} as ICidade),
  )
  const [destinoAutocomplete, setDestinoAutocomplete] = useState<ICidade>(
    destinoCompleto || ({} as ICidade),
  )
  const [produtos, setProdutos] = useState<IProduto[]>([])
  const [produtoInput, setProdutoInput] = useState(produto?.descricao || '')
  const [produtoAutocomplete, setProdutoAutocomplete] = useState(
    produto || ({} as IProduto),
  )
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState('')
  const debouncedOrigem = useDebounce(origemInput, 1000)
  const debouncedDestino = useDebounce(destinoInput, 1000)
  const history = useHistory()

  useEffect(() => {
    const init = async () => {
      if (
        debouncedOrigem &&
        origemInput !==
          `${origemAutocomplete?.nome} - ${origemAutocomplete?.uf_estado}`
      ) {
        setLoading(true)
        try {
          const response = await findByNome.execute(debouncedOrigem)
          setLoading(false)
          setCidadesOrigem(response.cidades)
        } catch (error) {
          setMessages(
            'Não foi possível carregar as cidades, por favor tente novamente em alguns minutos.',
          )
          setOpen(true)
          setLoading(false)
        }
      } else {
        setCidadesOrigem([])
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrigem, findByNome])

  useEffect(() => {
    const init = async () => {
      if (
        debouncedDestino &&
        destinoInput !==
          `${destinoAutocomplete?.nome} - ${destinoAutocomplete?.uf_estado}`
      ) {
        setLoading(true)
        try {
          const response = await findByNome.execute(debouncedDestino)
          setLoading(false)

          setCidadesDestino(response.cidades)
        } catch (error) {
          setMessages(
            'Não foi possível carregar as cidades, por favor tente novamente em alguns minutos.',
          )
          setOpen(true)
          setLoading(false)
        }
      } else {
        setCidadesDestino([])
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDestino, findByNome])

  useEffect(() => {
    const init = async () => {
      setLoading(true)

      try {
        const response = await findAllProdutos.execute()
        setProdutos(response)
        setLoading(false)
      } catch (error) {
        setMessages(
          'Não foi possível carregar os produtos, por favor tente novamente em alguns minutos.',
        )
        setOpen(true)
        setLoading(false)
      }
    }
    init()
  }, [findAllProdutos])

  const registrarEvento = () => {
    let mensagem = 'Tela de filtros '
    mensagem += origemInput ? `Origem: ${origemInput} ` : ''
    mensagem += produtoInput ? `Produto: ${produtoInput} ` : ''

    registerEvent({
      categoria: 'Consulta de Lotes (Filtro)',
      acao: 'Click Ok',
      local: mensagem,
    })
  }

  const handleSubmit = () => {
    registrarEvento()

    const valorFilterSlider = valorSlider as number
    const valorFilterSliderOrigem = valorSliderOrigem as number
    const valorFilterSliderDestino = valorSliderDestino as number

    idInput ? setId(idInput) : setId(null)

    if (origemAutocomplete?.nome) {
      setOrigem(origemAutocomplete.id)
      setOrigemCompleta(origemAutocomplete)
    } else {
      setOrigemCompleta({} as ICidade)
      setOrigem(null)
    }

    if (destinoAutocomplete?.nome) {
      setDestino(destinoAutocomplete.id)
      setDestinoCompleto(destinoAutocomplete)
    } else {
      setDestinoCompleto({} as ICidade)
      setDestino(null)
    }

    valorFilterSlider ? setValor(valorSlider as number) : setValor(null)
    valorFilterSliderOrigem !== null
      ? setRaioOrigem(valorSliderOrigem as number)
      : setRaioOrigem(null)
    valorFilterSliderDestino !== null
      ? setRaioDestino(valorSliderDestino as number)
      : setRaioDestino(null)

    setProduto(produtoAutocomplete)

    const ultimaConsulta = {
      loteId: +idInput,
      origem: origem,
      destino: destino,
      valor: valor,
      raioOrigem: raioOrigem,
      raioDestino: raioDestino,
      produto,
    }

    setLastFilter(ultimaConsulta)

    history.push('/lotes', {
      filtros: true,
    })
  }

  const handleOrigem = useCallback((cidade: string) => {
    setOrigemInput(cidade)
  }, [])

  const handleDestino = useCallback((cidade: string) => {
    setDestinoInput(cidade)
  }, [])

  const handleChangeSliderValue = (
    event: ChangeEvent<HTMLInputElement>,
    newValue: number | number[],
  ) => {
    setValorSlider(newValue)
  }

  const handleChangeSliderOrigemValue = (
    event: ChangeEvent<HTMLInputElement>,
    newValue: number | number[],
  ) => {
    setValorSliderOrigem(newValue)
  }

  const handleChangeSliderDestinoValue = (
    event: ChangeEvent<HTMLInputElement>,
    newValue: number | number[],
  ) => {
    setValorSliderDestino(newValue)
  }

  const handleId = (event: ChangeEvent<HTMLInputElement>) => {
    setIdInput(Number(event.target.value))
  }

  const handleProduto = (produto: string) => {
    setProdutoInput(produto)
  }

  const handleLotesDirecionados = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLotesDirecionados(event.target.checked)
  }

  const clearFilters = () => {
    setId(null)
    setOrigem(null)
    setDestino(null)
    setValor(null)
    setIdInput(null)
    setOrigemInput('')
    setOrigemAutocomplete({} as ICidade)
    setDestinoAutocomplete({} as ICidade)
    setOrigemCompleta({} as ICidade)
    setDestinoCompleto({} as ICidade)
    setDestinoInput('')
    setValorSlider(null)
    setValorSliderOrigem(100)
    setValorSliderDestino(100)
    setProdutoAutocomplete({} as IProduto)
    setProduto({} as IProduto)
    setProdutoInput('')
    setLotesDirecionados(false)
  }

  return (
    <ContainerPage
      breadCrumb="Filtros"
      title="Filtrar"
      showFooterScaffold={true}
      titleNextButton="Pesquisar"
      onClickNextButton={() => handleSubmit()}
    >
      <Form
        enableReinitialize
        onSubmit={() => handleSubmit()}
        initialValues={{
          numeroLote: idInput,
        }}
        validationSchema={() => ({
          numeroLote: Yup.string().nullable(),
        })}
      >
        {formControl => (
          <Grid container spacing={6}>
            <Grid item xs={12} md={5}>
              <Input
                placeholder="Digite número do lote"
                name="numeroLote"
                type="number"
                formControl={formControl}
                errorMessage={''}
                onChange={handleId}
                label={'Nº do lote'}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Slider
                id="slider-valor"
                value={valorSlider}
                onChange={handleChangeSliderValue}
                marks={marks}
                min={0}
                max={1000}
                label="Valor"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <ContainerAutocomplete>
                <Autocomplete
                  defaultValue={
                    origemAutocomplete?.nome ? origemAutocomplete : null
                  }
                  label={'Origem'}
                  placeholder="Digite para buscar"
                  onChange={(
                    event: ChangeEvent<HTMLInputElement>,
                    newValue: ICidade,
                  ) => {
                    setOrigemAutocomplete(newValue)
                  }}
                  inputValue={origemInput}
                  onInputChange={(_event, newInputValue) => {
                    handleOrigem(newInputValue)
                  }}
                  options={cidadesOrigem}
                  getOptionLabel={(option: ICidade) =>
                    `${option.nome} - ${option.uf_estado}`
                  }
                  loading={loading}
                  popupIcon={MdSearch}
                />
              </ContainerAutocomplete>
            </Grid>
            <Grid item xs={12} md={5}>
              <Slider
                value={valorSliderOrigem}
                defaultValue={100}
                onChange={handleChangeSliderOrigemValue}
                step={100}
                min={0}
                max={300}
                valueLabelDisplay="off"
                marks={marksRaio}
                label="Raio origem"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <ContainerAutocomplete>
                <Autocomplete
                  defaultValue={
                    destinoAutocomplete?.nome ? destinoAutocomplete : null
                  }
                  label={'Destino'}
                  placeholder="Digite para buscar"
                  onChange={(
                    event: ChangeEvent<HTMLInputElement>,
                    newValue: ICidade,
                  ) => {
                    setDestinoAutocomplete(newValue)
                  }}
                  inputValue={destinoInput}
                  onInputChange={(event, newInputValue) => {
                    handleDestino(newInputValue)
                  }}
                  options={cidadesDestino}
                  getOptionLabel={(option: ICidade) =>
                    `${option.nome} - ${option.uf_estado}`
                  }
                  loading={loading}
                  popupIcon={MdSearch}
                />
              </ContainerAutocomplete>
            </Grid>
            <Grid item xs={12} md={5}>
              <Slider
                value={valorSliderDestino}
                defaultValue={100}
                onChange={handleChangeSliderDestinoValue}
                step={100}
                min={0}
                max={300}
                valueLabelDisplay="off"
                marks={marksRaio}
                label="Raio destino"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <ContainerAutocomplete>
                <Autocomplete
                  defaultValue={produtoAutocomplete || null}
                  label={'Produto'}
                  placeholder="Digite para buscar"
                  onChange={(
                    event: ChangeEvent<HTMLInputElement>,
                    newValue: IProduto,
                  ) => {
                    setProdutoAutocomplete(newValue)
                  }}
                  inputValue={produtoInput}
                  onInputChange={(event, newInputValue) => {
                    handleProduto(newInputValue)
                  }}
                  options={produtos}
                  getOptionLabel={(option: IProduto) => option.descricao}
                  loading={loading}
                  popupIcon={MdSearch}
                />
              </ContainerAutocomplete>
            </Grid>
            <Grid item xs={12} md={5}>
              <LoteSwitch
                label="Listar apenas lotes direcionados"
                checked={lotesDirecionados}
                onChange={handleLotesDirecionados}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Button
                color={colors.white}
                fontColor={colors.orange}
                onClick={clearFilters}
              >
                Limpar filtro(s)
              </Button>
            </Grid>
          </Grid>
        )}
      </Form>
      <SnackBar
        open={open}
        message={messages}
        setOpen={setOpen}
        severity={'error'}
      />
    </ContainerPage>
  )
}

export default Filtros
